import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export type AvailableCreditsResponse = {
    AvailableCredit: string;
    CreditAmount: number;
    ExpiryDate: string;
};

import { emitWebviewEvent } from '@/components/tracking_third_party/utils/webview.utils';
import { TmAccount } from '@/tscapi/model/account';
import http from '@/tscapi/service/httpservice';
import { Cookie } from '@/tscapi/utils/cookie';
import {
    bubbleUnexpectedErrorsOnly,
    redirectToSignin,
    SessionExpiredError,
    SESSION_EXPIRED_MESSAGE,
} from '@/tscapi/utils/session';

export const useAccount = (isAuth: boolean) => {
    const cookie: Cookie = new Cookie();
    return useQuery({
        queryKey: ['account', cookie.UserAccessToken?.Edp, cookie.Language],
        queryFn: fetchAccount,
        useErrorBoundary: bubbleUnexpectedErrorsOnly,
        enabled: isAuth,
        retry: false,
        refetchOnWindowFocus: false,
    });

    async function fetchAccount(): Promise<TmAccount> {
        const base = '/api/v2/';
        const lang = cookie.Language.substring(0, 2) || 'en';
        const userAccessTokenEdp = cookie.UserAccessToken?.Edp;
        if (
            !(
                cookie.UserAccessToken != undefined &&
                cookie.UserAccessToken.Token.length > 0 &&
                cookie.UserAccessToken.Edp > 0
            )
        ) {
            await redirectToSignin({ reason: 'sessionexpired', signOutMessage: SESSION_EXPIRED_MESSAGE });
            throw new SessionExpiredError('UserToken expired');
        }
        try {
            const response = await http.get(`${base}${lang}/accounts/${userAccessTokenEdp}`);
            cookie.UserFraudStatus = response.data.IsFraud;
            cookie.UserAvailableCredit = response.data.HasAvailableCredit;
            cookie.NickName = response.data.Nickname ?? '';
            window.dispatchEvent(new CustomEvent('account:statusChanged'));
            window.dispatchEvent(new CustomEvent('account:usernameChanged'));
            return response.data;
        } catch (error) {
            // Already handled upstream (redirect/webview hand-off) - don't re-wrap into a generic error.
            if (error instanceof SessionExpiredError) {
                throw error;
            }
            //if this state happens, user will be re-directed to signin page anyway
            console.log('Error getting Account');
            throw new Error('Error getting Account');
        }
    }
};

export const useUpdatePassword = (CustomerEdp: number, showSuccess, handleAccordion) => {
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';

    type PasswordRequestPayload = {
        NewPassword: string;
        NewPasswordConfirmed: string;
        Username: string;
        CurrentPassword: string;
    };

    const updatePassword = (passwordRequestPayload: PasswordRequestPayload) => {
        return http.put(`${base}${lang}/accounts/${CustomerEdp}/password`, passwordRequestPayload);
    };
    return useMutation({
        onSuccess: async (response, variables, context) => {
            // setupAnalyticsData(response.data);
            emitWebviewEvent('passwordchange', {});
            showSuccess('password');
            handleAccordion(0, false);
        },
        onError: (error, variables, context) => {
            console.log('error');
        },
        mutationFn: updatePassword,
    });
};

export const useUpdateAccount = (CustomerEdp: number, showSuccess, handleAccordion) => {
    const base = '/api/v2/';
    const cookie: Cookie = new Cookie();
    const lang = cookie.Language.substring(0, 2) || 'en';
    const queryClient = useQueryClient();

    type AccountRequestPayload = {
        Nickname: string;
    };

    const updateAccount = async (accountRequestPayload: AccountRequestPayload): Promise<void> => {
        await http.put(`${base}${lang}/accounts/${CustomerEdp}`, accountRequestPayload);
    };

    return useMutation<void, unknown, AccountRequestPayload>({
        mutationFn: updateAccount,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['account'] });
            showSuccess('account');
            handleAccordion(2, false);
        },
        onError: (error) => {
            console.log('error', error);
        },
    });
};

export const useAvailableCredits = (customerEdp: number, hasAvailableCredit: boolean) => {
    const cookie: Cookie = new Cookie();
    const base = '/api/v2/';
    const lang = cookie.Language.substring(0, 2) || 'en';

    return useQuery<AvailableCreditsResponse[]>({
        queryKey: ['availableCredits', customerEdp],
        queryFn: async () => {
            const response = await http.get(`${base}${lang}/accounts/${customerEdp}/availablecredits`, {
                skipResponseInterceptor: true,
            } as any);
            const credits = response.data as AvailableCreditsResponse[];

            // Set cookie based on credit
            if (credits && credits.length > 0) {
                cookie.TSC_HasCredit = true;
            } else {
                cookie.TSC_HasCredit = false;
            }

            return credits;
        },
        enabled: hasAvailableCredit && customerEdp > 0,
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        useErrorBoundary: false,
    });
};
